import type { SaveAdapter } from "./SaveAdapter.js";

/**
 * 주기 flush 타이머 추상 — 테스트 결정론을 위해 주입 가능.
 * `start(intervalMs, cb)`는 intervalMs마다 cb를 호출하는 반복 타이머를 시작하고 정지 함수를 반환한다.
 */
export interface WriteBehindScheduler {
  start(intervalMs: number, cb: () => void): () => void;
}

/** 기본 스케줄러 — 브라우저/Node 공통 setInterval. */
const defaultScheduler: WriteBehindScheduler = {
  start(intervalMs, cb) {
    const id = setInterval(cb, intervalMs);
    return () => clearInterval(id);
  },
};

export interface WriteBehindOptions {
  /** 클라우드 flush 주기(ms). 기본 60s — 호출자의 잦은 save()보다 길게 둬 batch 효과. */
  flushIntervalMs?: number;
  /** flush 타이머 스케줄러(테스트 주입). 기본 setInterval. */
  scheduler?: WriteBehindScheduler;
  /** flush 중 cloud 쓰기 실패 hook(오프라인 디버깅용, 선택). 실패 항목은 다음 주기 재시도된다. */
  onFlushError?: (key: string, error: unknown) => void;
}

/**
 * 클라우드 ack 미완 삭제 예약(tombstone)을 로컬 계층에 영속하는 메타 키.
 * 게임 세이브 키와 충돌하지 않게 네임스페이스 prefix 사용.
 */
const TOMBSTONE_META_KEY = "__writeBehind.pendingDeletes__";

/**
 * Write-behind SaveAdapter (idleblade BufferedFirestoreSaveLogic 패턴의 TS 이식).
 *
 * - 그 자체가 `SaveAdapter`라 호출자(게임)는 로컬/클라우드 조합을 모른 채 save/load/delete만 쓴다
 *   ("읽고 쓰는 것만 추상 의존, 로컬이냐 클라우드냐는 외부 주입으로 결정").
 * - save: 로컬에 즉시 저장 + dirty 마킹 → 클라우드 전송은 보류(batch). 잦은 save가 클라우드 쓰기로 직결되지 않음.
 * - load: 로컬 우선(미flush 변경 포함 최신) → 없으면 클라우드 fallback + 로컬 캐시(새 기기/재설치 복원).
 *   단 삭제 예약(tombstone) 중인 key는 클라우드 사본을 보지 않는다(삭제한 세이브 부활 차단).
 * - delete: 로컬 즉시 삭제 + 삭제 예약 영속 후 **즉시 resolve** — 클라우드 ack를 기다리지 않는다.
 *   (Firestore deleteDoc은 오프라인이면 reject가 아니라 서버 ack까지 무기한 pending — 여기에 await로
 *   묶이면 호출자(계정삭제→scene.restart)가 영영 대기하는 "무반응"이 된다. 2026-06-12 실버그.)
 *   클라우드 삭제는 fire-and-track으로 발사하고, 성공 시 예약 해제 / 실패·미완은 flush가 재시도.
 * - flush: 삭제 예약 재시도 + dirty 항목 클라우드 전송. 성공분만 제거 → 실패분은 다음 주기 자동 재시도(오프라인 큐잉).
 * - flushIntervalMs 주기 타이머가 자동 flush. dispose()로 타이머 정지 + 최종 flush.
 *
 * 멀티기기 동시 플레이 시 로컬 우선 로드는 stale 가능(단일기기 가정 — idleblade 동일). LWW가 필요하면
 * payload의 타임스탬프(예: lastSavedAtUtcMs)를 비교하는 load 전략으로 확장한다.
 */
export class WriteBehindSaveAdapter implements SaveAdapter {
  /** 아직 클라우드로 flush되지 않은 변경분(key → 최신 payload). */
  private readonly pending = new Map<string, string>();
  /** 클라우드 ack 미완 삭제 예약 — load 부활 차단 + flush 재시도 대상. 로컬에 영속(재부팅 생존). */
  private readonly pendingDeletes = new Set<string>();
  /** 진행 중인 클라우드 delete(key) — 무응답 클라우드에 중복 발사 방지. */
  private readonly inFlightDeletes = new Set<string>();
  /** 영속된 삭제 예약 1회 로드 게이트 — 모든 공개 연산이 선행 await. */
  private tombstonesLoaded: Promise<void> | null = null;
  /** 예약 영속 직렬화 체인 — 동시 다발 persist가 구버전 스냅샷으로 덮어쓰는 race 방지. */
  private tombstonePersistChain: Promise<void> = Promise.resolve();
  private readonly stopTimer: () => void;
  private readonly onFlushError?: (key: string, error: unknown) => void;
  private disposed = false;

  constructor(
    private readonly local: SaveAdapter,
    private readonly cloud: SaveAdapter,
    options: WriteBehindOptions = {},
  ) {
    const intervalMs = options.flushIntervalMs ?? 60_000;
    const scheduler = options.scheduler ?? defaultScheduler;
    this.onFlushError = options.onFlushError;
    this.stopTimer = scheduler.start(intervalMs, () => {
      void this.flush();
    });
  }

  /** 영속된 삭제 예약을 1회 로드 (부재/손상은 빈 예약으로 시작). */
  private ensureTombstones(): Promise<void> {
    this.tombstonesLoaded ??= (async () => {
      try {
        const raw = await this.local.load(TOMBSTONE_META_KEY);
        const parsed: unknown = raw !== null ? JSON.parse(raw) : null;
        if (Array.isArray(parsed)) {
          for (const key of parsed) {
            if (typeof key === "string") this.pendingDeletes.add(key);
          }
        }
      } catch {
        /* 손상/로드 실패 — 빈 예약으로 동작 (클라우드 사본이 남을 수 있으나 기능은 유지) */
      }
    })();
    return this.tombstonesLoaded;
  }

  /**
   * 삭제 예약을 로컬에 영속 (비어 있으면 메타 키 제거). 실패는 무시 — 메모리 예약으로 동작.
   * 직렬화 체인으로 순차 실행 — 동시 다발 호출(flush의 다중 key kickCloudDelete 성공 콜백 등)이
   * 각자의 스냅샷으로 I/O race를 일으켜 최신 예약을 구버전으로 덮어쓰는 것을 방지한다.
   * 스냅샷은 실행 시점에 읽으므로 체인 뒤 실행분이 항상 최신 Set을 반영한다.
   */
  private persistTombstones(): Promise<void> {
    this.tombstonePersistChain = this.tombstonePersistChain.then(async () => {
      try {
        if (this.pendingDeletes.size === 0) await this.local.delete(TOMBSTONE_META_KEY);
        else await this.local.save(TOMBSTONE_META_KEY, JSON.stringify([...this.pendingDeletes]));
      } catch {
        /* 영속 실패 — 이번 세션 메모리 예약만으로 동작 */
      }
    });
    return this.tombstonePersistChain;
  }

  /**
   * 클라우드 삭제 발사 (fire-and-track) — resolve를 기다리지 않는다. 성공 시 예약 해제 + 영속,
   * 실패는 onFlushError로 알리고 예약 유지(다음 flush 재시도). 무응답(pending) 동안 중복 발사 방지.
   */
  private kickCloudDelete(key: string): void {
    if (this.inFlightDeletes.has(key)) return;
    this.inFlightDeletes.add(key);
    this.cloud
      .delete(key)
      .then(
        () => {
          this.inFlightDeletes.delete(key);
          this.pendingDeletes.delete(key);
          void this.persistTombstones();
        },
        (error: unknown) => {
          this.inFlightDeletes.delete(key);
          this.onFlushError?.(key, error);
        },
      );
  }

  async save(key: string, payload: string): Promise<void> {
    await this.ensureTombstones();
    if (this.pendingDeletes.delete(key)) await this.persistTombstones(); // 재생성 — 삭제 예약 해제
    await this.local.save(key, payload); // 로컬 즉시(SSoT)
    this.pending.set(key, payload); // 클라우드는 다음 flush에서 batch 전송
  }

  async load(key: string): Promise<string | null> {
    const localValue = await this.local.load(key);
    if (localValue !== null) return localValue; // 로컬 우선
    await this.ensureTombstones();
    if (this.pendingDeletes.has(key)) return null; // 삭제 예약 — 클라우드 사본 부활 차단
    // 로컬 미스(새 기기/재설치) → 클라우드 fallback + 로컬 캐시
    const cloudValue = await this.cloud.load(key);
    if (cloudValue !== null) {
      try {
        await this.local.save(key, cloudValue);
      } catch {
        /* 캐시 실패는 무시 — 다음 save가 다시 채운다 */
      }
    }
    return cloudValue;
  }

  async delete(key: string): Promise<void> {
    await this.ensureTombstones();
    this.pending.delete(key);
    await this.local.delete(key);
    this.pendingDeletes.add(key);
    await this.persistTombstones();
    // ⚠ 클라우드 ack를 await하지 않는다 — Firestore deleteDoc은 오프라인이면 무기한 pending이라
    // 호출자(계정삭제→restart)가 영영 묶인다. 예약+영속이 끝났으므로 여기서 즉시 완료.
    this.kickCloudDelete(key);
  }

  /** 삭제 예약 재시도 + pending 변경분 클라우드 전송. 성공분만 제거(실패=다음 주기 재시도). */
  async flush(): Promise<void> {
    await this.ensureTombstones();
    // 삭제 예약 재발사 — 무응답으로 in-flight면 skip(중복 방지), 완료/실패 후 다음 주기 재시도.
    for (const key of [...this.pendingDeletes]) this.kickCloudDelete(key);
    if (this.pending.size === 0) return;
    for (const [key, payload] of [...this.pending.entries()]) {
      try {
        await this.cloud.save(key, payload);
        // flush 도중 같은 key가 다시 save돼 payload가 바뀌었으면 제거하지 않는다(최신 변경 보존).
        if (this.pending.get(key) === payload) this.pending.delete(key);
      } catch (error) {
        this.onFlushError?.(key, error); // pending 유지 → 다음 주기/온라인 복구 시 재전송
      }
    }
  }

  /** 타이머 정지 + 최종 flush(앱 종료/백그라운드 시 클라우드 반영 보장). */
  async dispose(): Promise<void> {
    if (this.disposed) return;
    this.disposed = true;
    this.stopTimer();
    await this.flush();
  }
}
